(() => {
  const selectors = {
    drawer: '[data-cart-drawer]',
    drawerItems: '[data-cart-drawer-items]',
    drawerSubtotal: '[data-cart-drawer-subtotal]',
    drawerCount: '[data-cart-count]',
    addToCartForm: 'form[data-kizuna-add-to-cart]',
    openCart: '[data-cart-open]',
    closeCart: '[data-cart-close]',
    predictiveSearch: '[data-predictive-search]'
  };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format((cents || 0) / 100);
    } catch (error) {
      return `${((cents || 0) / 100).toFixed(2)} EUR`;
    }
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);

  const drawer = document.querySelector(selectors.drawer);

  const openDrawer = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    drawer.querySelector(selectors.closeCart)?.focus();
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  };

  const renderCart = (cart) => {
    document.querySelectorAll(selectors.drawerCount).forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });

    const itemsContainer = drawer?.querySelector(selectors.drawerItems);
    const subtotal = drawer?.querySelector(selectors.drawerSubtotal);

    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    if (!itemsContainer) return;

    if (!cart.items.length) {
      itemsContainer.innerHTML = '<p class="empty-state">Dein Warenkorb ist leer.</p>';
      return;
    }

    itemsContainer.innerHTML = cart.items.map((item) => {
      const image = item.featured_image?.url
        ? `<img src="${escapeHtml(item.featured_image.url)}" alt="${escapeHtml(item.featured_image.alt || item.product_title)}" loading="lazy">`
        : '<div class="cart-item__placeholder" aria-hidden="true"></div>';

      return `
        <article class="cart-item" data-cart-line-key="${escapeHtml(item.key)}">
          ${image}
          <div class="cart-item__body">
            <strong>${escapeHtml(item.product_title)}</strong>
            ${item.variant_title ? `<span class="muted">${escapeHtml(item.variant_title)}</span>` : ''}
            <span>${formatMoney(item.final_line_price)}</span>
            <div class="cart-item__actions">
              <span class="muted">Menge ${item.quantity}</span>
              <button type="button" data-cart-remove="${escapeHtml(item.key)}">Entfernen</button>
            </div>
          </div>
        </article>
      `;
    }).join('');
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  document.addEventListener('click', async (event) => {
    const openButton = event.target.closest(selectors.openCart);
    const closeButton = event.target.closest(selectors.closeCart);
    const removeButton = event.target.closest('[data-cart-remove]');

    if (openButton) {
      event.preventDefault();
      try {
        renderCart(await fetchCart());
      } finally {
        openDrawer();
      }
    }

    if (closeButton) {
      event.preventDefault();
      closeDrawer();
    }

    if (removeButton) {
      event.preventDefault();
      const key = removeButton.getAttribute('data-cart-remove');
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (response.ok) renderCart(await response.json());
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest(selectors.addToCartForm);
    if (!form) return;

    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error('Add to cart failed');
      renderCart(await fetchCart());
      openDrawer();
    } catch (error) {
      form.submit();
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  });

  document.querySelectorAll(selectors.predictiveSearch).forEach((root) => {
    const input = root.querySelector('input[type="search"]');
    const results = root.querySelector('[data-predictive-results]');
    let controller;

    if (!input || !results) return;

    input.addEventListener('input', async () => {
      const query = input.value.trim();

      if (controller) controller.abort();
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }

      controller = new AbortController();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) return;

        const data = await response.json();
        const products = data.resources?.results?.products || [];

        results.innerHTML = products.length
          ? products.map((product) => `
              <a class="predictive-search__item" href="${escapeHtml(product.url)}">
                ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<span class="predictive-search__thumb"></span>'}
                <span>
                  <strong>${escapeHtml(product.title)}</strong>
                  <span class="muted">${escapeHtml(product.price || '')}</span>
                </span>
              </a>
            `).join('')
          : '<p class="muted">Keine passenden Produkte gefunden.</p>';
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    });
  });

  window.kizunaAnalytics = window.kizunaAnalytics || {
    track(eventName, payload = {}) {
      window.dispatchEvent(new CustomEvent('kizuna:analytics', {
        detail: { eventName, payload, consentRequired: true }
      }));
    }
  };

  // Scroll-reveal for .reveal elements and the vision section's bond-line SVG paths.
  const revealTargets = document.querySelectorAll('.reveal, .bond-line');
  if (revealTargets.length && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    revealTargets.forEach((target) => revealObserver.observe(target));
  } else {
    revealTargets.forEach((target) => target.classList.add('is-visible'));
  }
})();
